import { useState, useEffect } from 'react';
import { useAccounts } from '../../context/AccountContext';
import { useTranslation } from "react-i18next";
import { useTestPassword } from '../../hooks/PasswordHooks';
import { useViewport } from '../../hooks/useViewport';
import { PasswordParams } from '../../types/PasswordParams';
import { ResultPanel } from '../ResultPanel'
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Card } from '@uhg-abyss/web/ui/Card';
import { HelpMeAlert } from '../HelpMeAlert';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { styled } from '@uhg-abyss/web/tools/styled';
import SelectedAccountInfoBox from '../SelectedAccountInfoBox';

const PasswordInput = styled(TextInput, {
    '&.abyss-text-input': {
        border: '1px solid $gray4',
        minHeight: 40,
    },
});

const SubmitButton = styled(Button, {
    '&.abyss-button-root': {
        backgroundColor: '$interactiveNav',
        borderColor: '$interactiveNav',
        color: '$textOnDark',
        '&:hover': {
            backgroundColor: '$surfaceHeroHover',
            borderColor: '$surfaceHeroHover',
            color: '$textPrimary',
            boxShadow: '1px 1px 1px #dadbdc',
        },
        '&[aria-disabled="true"]': {
            backgroundColor: '$surfaceDisabled',
            borderColor: '$surfaceDisabled',
            color: '$textDisabled',
            boxShadow: '1px 1px 1px #dadbdc',
        },
    },
});

const SubmitButtonBlock = styled('div', { padding: 15, margin: 4 });

export const TestPassword = () => {
    const { isTabletOrMobile, isPortrait } = useViewport();

    const textInputWidth = isTabletOrMobile
        ? isPortrait
            ? 216
            : 280
        : 360;  //default

    const headingOffset = isTabletOrMobile
        ? 5   //h6 heading
        : 4;  //default, h5 heading

    const cardBorderRadius = isTabletOrMobile ? 5 : 10;
    const cardPadding = isTabletOrMobile ? 5 : 15;
    const cardHdrMarginBottom = isTabletOrMobile ? 0 : 16;

    const { selectedAccount } = useAccounts();
    const { t } = useTranslation();
    const domainName = selectedAccount?.resourceName ?? 'MS';
    const [isSubmitDisabled, setSubmitDisabled] = useState(true);
    const [hasError, setError] = useState(false);
    const [isCardHidden, setCardHidden] = useState(false);
    const [isSuccess, setSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasInitializedFocus, setHasInitializedFocus] = useState(false);

    const testPasswordForm = useForm({
        defaultValues: {
            password: '',
        },
    });

    const resetForm = () => {
        testPasswordForm.reset();
        setSubmitDisabled(true);
        setError(false);
        setCardHidden(false);
        setSuccess(false);
        setIsLoading(false);
        setHasInitializedFocus(false);
    };

    useEffect(() => {
        // Apply auto-focus once on initial load; do not steal focus from global nav controls.
        if (!hasInitializedFocus) {
            testPasswordForm.setFocus('password');
            setHasInitializedFocus(true);
        }
    }, [hasInitializedFocus, testPasswordForm]);

    const onInputChange = (e: { target: { name: string; value: string; }; }) => {
        const { value } = e.target;

        setSubmitDisabled(isLoading === true || !(value && value.length > 0));
    }

    const testPasswordMutation = useTestPassword();

    const onSubmit = (data: { password: string; }, e?: React.BaseSyntheticEvent) => {
        if (!selectedAccount) {
            // Should never happen: accounts should only be empty on an upstream API failure.
            console.error('TestPassword: submit attempted with no active account');
            setCardHidden(false);
            setError(true);
            setSubmitDisabled(false);
            return;
        }
        setIsLoading(true);  //start spinner
        setSubmitDisabled(true);
        const params: PasswordParams = {
            password: btoa(data.password), //Built in function to encode in base64
            resourceId: selectedAccount.resourceId
        };
        testPasswordMutation.mutate(params,
            {
                onSuccess: (response: { data: { Response: string; }; }) => {
                    setIsLoading(false);  //stop spinner
                    if (response.data.Response.toLowerCase() === "success") {
                        setCardHidden(true);
                        setError(false);
                        setSuccess(true);
                    } else if (response.data.Response.toLowerCase() === "invalidpassword") {
                        setCardHidden(true);
                        setError(false);
                        setSuccess(false);
                    } else {
                        setCardHidden(false);
                        setError(true);
                        setSubmitDisabled(false);
                        setSuccess(false);
                    }
                },
                onError: () => {
                    setIsLoading(false);  //stop spinner
                    setCardHidden(false);
                    setError(true);
                    setSubmitDisabled(false);
                    setSuccess(false);
                },
            });
    };

    const handleLinkClick = () => {
        setSubmitDisabled(false);
        setCardHidden(false);  //can hard-code to false here because the event handler is currently only used on the 'Try Again' link
    };

    return (
        <Layout.Stack alignLayout="center">
            {hasError && <HelpMeAlert
                status="error"
                title={t('testPasswordError1')}
            >
                {t('testPasswordError2')}
            </HelpMeAlert>}

            {!isCardHidden && <Card
                css={{
                    'abyss-card-root': {
                        border: '1px solid $gray4',
                        boxShadow: 'none',
                        borderRadius: `${cardBorderRadius}px`,
                        padding: `${cardPadding}px`,
                        margin: 4,
                    }
                }}
            >
                <div>
                    <div style={{ border: 0, padding: `${cardPadding}px`, margin: 4, }}>
                        <Layout.Stack alignLayout="center">
                            <Heading
                                level={headingOffset}
                                color="$textPrimary"
                                style={{ marginBottom: `${cardHdrMarginBottom}` }}
                                data-testid="testMSIDPasswordHeading"
                            >{t('testPassword')}
                            </Heading>
                            <LoadingSpinner isLoading={isLoading} ariaLoadingLabel="Testing password" />
                        </Layout.Stack>
                    </div>
                    <FormProvider state={testPasswordForm} onSubmit={onSubmit} >
                        <Layout.Stack alignLayout="center">
                            <SelectedAccountInfoBox
                                onSelect={resetForm}
                            />
                            
                            <PasswordInput
                                label={t('password')}
                                hideLabel
                                placeholder={t('password')}
                                type="password"
                                model="password"
                                autoComplete="new-password"
                                isDisabled={isLoading}
                                isClearable
                                onChange={onInputChange}
                                onClear={() => setSubmitDisabled(true)}
                                width={`${textInputWidth}px`}
                                data-testid="passwordInput"
                            />

                            <SubmitButtonBlock>
                                <SubmitButton
                                    type="submit"
                                    isDisabled={isSubmitDisabled}
                                    size="$md"
                                    data-testid="testButton">{t('test')}</SubmitButton>
                            </SubmitButtonBlock>
                        </Layout.Stack>
                    </FormProvider>
                </div>
            </Card>}
            {isSuccess && <ResultPanel
                messageId="passwordCorrectMsg"
                message={t('passwordCorrectMsg', { domainName })}
                icon="check_circle"
                iconColor="$statusSuccess"
                linkId="goHomeLink"
                linkTo="/"
                linkText={t('goHome')}
            />}
            {!isSuccess && isCardHidden && <ResultPanel
                messageId="passwordIncorrectMsg"
                message={t('passwordIncorrectMsg', { domainName })}
                icon="remove_circle"
                iconColor="$statusError"
                linkId="tryAgainLink"
                linkTo="/test-password"
                linkText={t('tryAgain')}
                onLinkClick={handleLinkClick}
            />}
        </Layout.Stack>
    );
}
